import React, { useState, useEffect, useMemo } from 'react';

import { Button } from '../../components/general';
import { ConfirmModal } from '../../components/modals';

import { EVENT_TYPE_BRAND } from '../../constants';
import { PublicEvent, Face } from '../../types';
import { isEmpty } from '../../utils';

import CheckinStepper from './components/checkinStepper';
import DetailsStage from './stages/detailsStage';
import EmailStage from './stages/emailStage';
import PhotoStage from './stages/photoStage';
import VideoStage from './stages/videoStage';

interface Props {
  isPreview?: boolean;
  width: number;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  event: PublicEvent;
  face: Face;
  refetchFace?: () => void;
}

const CheckInContent: React.FC<Props> = (props) => {
  const { isPreview, width, step, setStep, event, face, refetchFace } = props;

  const [showExistingAttModal, setShowExistingAttModal] = useState(false);

  const isBrand = event.type === EVENT_TYPE_BRAND;

  const hasMinAssets = useMemo(() => {
    // for brand flow – there should be enough uploaded videos to fill required wildcards
    if (isBrand)
      return (face.videos?.length ?? 0) >= (event.wildcards?.length ?? 0);
    // for videographer flow – there should be a photo
    else return !isEmpty(face.photos);
  }, [event.wildcards, face, isBrand]);

  useEffect(() => {
    if (hasMinAssets) setShowExistingAttModal(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNextStep = () => {
    setStep((prev: number) => prev + 1);
  };

  const renderContent = () => {
    // brand steps
    if (isBrand) {
      switch (step) {
        case 0:
          return (
            <EmailStage event={event} isPreview={isPreview} width={width} />
          );

        case 1:
          return (
            <VideoStage
              event={event}
              face={face}
              handleAfterUpload={handleNextStep}
              refetchFace={refetchFace}
              isPreview={isPreview}
            />
          );

        case 2:
          return (
            <>
              <PhotoStage
                event={event}
                face={face}
                refetchFace={refetchFace}
                stepText={1}
                hidePoints
                isPreview={isPreview}
              />

              <hr className="w-full" />

              <DetailsStage
                event={event}
                face={face}
                refetchFace={refetchFace}
                isPreview={isPreview}
                stepText={2}
              />
            </>
          );

        default:
          return null;
      }

      // videographer steps
    } else {
      switch (step) {
        case 0:
          return (
            <EmailStage event={event} isPreview={isPreview} width={width} />
          );

        case 1:
          return (
            <>
              <PhotoStage
                event={event}
                face={face}
                handleAfterUpload={!hasMinAssets ? handleNextStep : undefined}
                refetchFace={refetchFace}
                isPreview={isPreview}
              />

              {hasMinAssets && (
                <Button className="w-full" onClick={handleNextStep}>
                  Next Step
                </Button>
              )}
            </>
          );

        case 2:
          return (
            <DetailsStage
              event={event}
              face={face}
              refetchFace={refetchFace}
              isPreview={isPreview}
            />
          );

        default:
          return null;
      }
    }
  };

  return (
    <React.Fragment>
      <h5 className="w-full text-center font-normal">
        <b>Check-in</b> to generate your personalised video at{' '}
        <b>{event.name}</b>
      </h5>

      <CheckinStepper
        activeStep={step}
        setStep={hasMinAssets ? setStep : undefined}
      />

      {renderContent()}

      <ConfirmModal
        show={showExistingAttModal}
        onHide={() => setShowExistingAttModal(false)}
        heading="No worries, you’re already checked in"
        subheading={
          <div>
            <b>You don’t need to check in again</b>, but you can still upload
            additional {isBrand ? 'videos' : 'photo'} or update your details in
            the next step.
          </div>
        }
        btns={[
          {
            label: `Upload ${isBrand ? 'Videos' : 'Photo'}`,
            onClick: () => setShowExistingAttModal(false),
          },
          {
            label: 'Update Details',
            onClick: () => {
              setShowExistingAttModal(false);
              handleNextStep();
            },
          },
        ]}
      />
    </React.Fragment>
  );
};

export default CheckInContent;
